import { configureStore } from "@reduxjs/toolkit";
import { 
  persistStore, 
  persistReducer, 
  FLUSH, 
  REHYDRATE, 
  PAUSE, 
  PERSIST, 
  PURGE, 
  REGISTER,
  createTransform
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import type { WebSocketState } from "./slices/websocketSlice";
import rootReducer from "./reducers";

// Create a transform that excludes websocket state from persistence
// and provides default websocket state on rehydration
const websocketTransform = createTransform(
  // Transform state on its way to being serialized and persisted (outbound)
  (_inboundState: any) => {
    // Don't persist websocket state
    return undefined;
  },
  // Transform state being rehydrated (inbound)
  (_outboundState: any) => {
    // Return default websocket state
    const defaultWebSocketState: WebSocketState = {
      connection: {
        isConnected: false,
        clientId: null,
        error: null,
        reconnectAttempts: 0,
      },
      vehiclePositions: {},
      vehicleStatuses: {},
      alerts: [],
      channels: {},
      shouldAutoReconnect: true,
    };
    return defaultWebSocketState;
  },
  // Only apply this transform to the websocket slice
  { whitelist: ['websocket'] }
);

const persistConfig = {
  key: "root",
  storage,
  transforms: [websocketTransform],
  // Alternative approach: just blacklist websocket entirely
  // blacklist: ['websocket']
};

const persistedReducer = persistReducer<any>(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        ignoredActionsPaths: ['payload.timestamp'],
        ignoredPaths: ['websocket.connection.timestamp'],
      },
    }),
});

const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export { store, persistor };